<script setup lang="ts">
import { onMounted, ref } from 'vue'

import { api, messageFrom } from '../api'
import { formatBytes, formatDuration } from '../format'
import type {
  VoiceCandidate,
  VoiceCandidatesResponse,
} from '../types'

const emit = defineEmits<{ navigate: [path: string] }>()
const DEFAULT_PREVIEW_TEXT = 'Так, ну чего, погнали. Сейчас спокойно разберёмся, что здесь происходит, и сделаем всё как надо.'

const speakers = ref<VoiceCandidatesResponse['speakers']>([])
const previewText = ref(DEFAULT_PREVIEW_TEXT)
const loading = ref(true)
const scanning = ref(false)
const busyCandidateId = ref('')
const busyAction = ref<'preview' | 'apply' | null>(null)
const scanMessage = ref('')
const error = ref('')

async function load(silent = false): Promise<void> {
  if (!silent) loading.value = true
  try {
    speakers.value = (await api<VoiceCandidatesResponse>('/api/admin/voice-candidates')).speakers
    error.value = ''
  } catch (reason) { error.value = messageFrom(reason) }
  finally { loading.value = false }
}

async function scan(): Promise<void> {
  if (scanning.value || busyCandidateId.value) return
  scanning.value = true
  scanMessage.value = ''
  try {
    const response = await api<VoiceCandidatesResponse & {
      scan: { added: number; removed: number; total: number }
    }>('/api/admin/voice-candidates/scan', { method: 'POST', body: '{}' })
    speakers.value = response.speakers
    scanMessage.value = `Готово: добавлено ${response.scan.added}, удалено ${response.scan.removed}, всего ${response.scan.total}.`
  } catch (reason) { window.alert(messageFrom(reason)) }
  finally { scanning.value = false }
}

function replaceCandidate(candidate: VoiceCandidate): void {
  const speaker = speakers.value.find(item => item.candidates.some(current => current.id === candidate.id))
  if (!speaker) return
  const index = speaker.candidates.findIndex(current => current.id === candidate.id)
  if (index < 0) return
  speaker.candidates[index] = {
    ...candidate,
    previewUrl: candidate.previewUrl ? `${candidate.previewUrl}?v=${Date.now()}` : null,
  }
}

async function preview(candidate: VoiceCandidate): Promise<void> {
  if (busyCandidateId.value || scanning.value) return
  if (!previewText.value.trim()) {
    window.alert('Введите текст пробной озвучки.')
    return
  }
  busyCandidateId.value = candidate.id
  busyAction.value = 'preview'
  try {
    const response = await api<{ candidate: VoiceCandidate }>(
      `/api/admin/voice-candidates/${candidate.id}/preview`,
      { method: 'POST', body: JSON.stringify({ text: previewText.value }) },
    )
    replaceCandidate(response.candidate)
  } catch (reason) { window.alert(messageFrom(reason)) }
  finally {
    busyCandidateId.value = ''
    busyAction.value = null
  }
}

async function apply(candidate: VoiceCandidate): Promise<void> {
  if (busyCandidateId.value || scanning.value || candidate.isActive) return
  busyCandidateId.value = candidate.id
  busyAction.value = 'apply'
  try {
    speakers.value = (await api<VoiceCandidatesResponse>(
      `/api/admin/voice-candidates/${candidate.id}/apply`,
      { method: 'POST', body: '{}' },
    )).speakers
  } catch (reason) { window.alert(messageFrom(reason)) }
  finally {
    busyCandidateId.value = ''
    busyAction.value = null
  }
}

function previewButtonText(candidate: VoiceCandidate): string {
  if (busyCandidateId.value !== candidate.id || busyAction.value !== 'preview') return 'Послушать пробу'
  return candidate.voiceRegistered ? 'Озвучиваем…' : 'Регистрируем голос…'
}

function applyButtonText(candidate: VoiceCandidate): string {
  if (candidate.isActive) return 'Звучит сейчас'
  if (busyCandidateId.value !== candidate.id || busyAction.value !== 'apply') return 'Сделать голосом'
  return candidate.voiceRegistered ? 'Применяем…' : 'Регистрируем голос…'
}

onMounted(load)
</script>

<template>
  <main class="page page--voice">
    <button class="back-link" type="button" @click="emit('navigate', '/admin')">← К папкам спикеров</button>
    <header class="page-heading page-heading--actions">
      <div>
        <p class="eyebrow">Озвучка ответов</p>
        <h1>Голос</h1>
      </div>
      <button
        class="button button--quiet"
        type="button"
        :disabled="scanning || Boolean(busyCandidateId)"
        @click="scan"
      >
        {{ scanning ? 'Обновляем список…' : 'Обновить список' }}
      </button>
    </header>

    <section class="settings-card voice-preview-settings">
      <label for="voice-preview-text">Текст пробной озвучки</label>
      <textarea id="voice-preview-text" v-model="previewText" rows="4" maxlength="2000" />
      <p class="settings-hint">
        Один текст используется для всех вариантов. Если его изменить, проба будет озвучена заново.
      </p>
    </section>

    <p v-if="scanMessage" class="text-success">{{ scanMessage }}</p>
    <p v-if="loading" class="quiet-message">Загружаем образцы голоса…</p>
    <p v-else-if="!speakers.length" class="empty-state">
      Кандидатов пока нет. Положите MP3 или WAV в папки voice-candidates и обновите список.
    </p>

    <template v-else>
      <section v-for="speaker in speakers" :key="speaker.id" class="voice-speaker-section">
        <div class="section-heading">
          <div>
            <p class="eyebrow">Спикер</p>
            <h2>{{ speaker.name }}</h2>
          </div>
          <strong :class="speaker.voiceConfigured ? 'text-success' : ''">
            {{ speaker.voiceConfigured ? 'Голос задан' : 'Голос не задан' }}
          </strong>
        </div>

        <p v-if="!speaker.candidates.length" class="empty-state">
          Текущий голос задан со страницы спикера. Кандидатов в папке пока нет.
        </p>
        <div v-else class="voice-candidate-grid">
          <article v-for="candidate in speaker.candidates" :key="candidate.id" class="settings-card voice-candidate-card">
            <div class="voice-candidate-heading">
              <div>
                <p class="eyebrow">Вариант голоса</p>
                <h2>{{ candidate.label }}</h2>
              </div>
              <strong v-if="candidate.isActive" class="text-success">Звучит сейчас</strong>
            </div>
            <p class="voice-candidate-meta">
              {{ candidate.fileName }} · {{ formatDuration(candidate.durationSec) }} ·
              {{ candidate.sizeBytes === null ? 'Размер уточняется' : formatBytes(candidate.sizeBytes) }}
            </p>
            <audio controls preload="none" :src="candidate.sampleUrl">
              Браузер не поддерживает аудиоплеер.
            </audio>
            <div v-if="candidate.previewUrl" class="test-result">
              <strong>Пробная озвучка</strong>
              <audio controls preload="none" :src="candidate.previewUrl">
                Браузер не поддерживает аудиоплеер.
              </audio>
            </div>
            <div class="button-row">
              <button
                class="button button--quiet"
                type="button"
                :disabled="scanning || Boolean(busyCandidateId)"
                @click="preview(candidate)"
              >
                {{ previewButtonText(candidate) }}
              </button>
              <button
                class="button button--primary"
                type="button"
                :disabled="candidate.isActive || scanning || Boolean(busyCandidateId)"
                @click="apply(candidate)"
              >
                {{ applyButtonText(candidate) }}
              </button>
            </div>
          </article>
        </div>
      </section>
    </template>

    <p v-if="error" class="field-error" role="alert">{{ error }}</p>
  </main>
</template>
